import React, { useState } from 'react';
import { Box, Button, TextField, Typography, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Tab, Tabs, Divider } from '@mui/material';
import { DownloadOutlined, AssessmentOutlined, PhoneAndroidOutlined } from '@mui/icons-material';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { getSMSReport, getPackageReport } from '../utils/api';

// ── Cell style helpers ────────────────────────────────────────
const border = {
  top:    { style: 'thin', color: { rgb: '000000' } },
  bottom: { style: 'thin', color: { rgb: '000000' } },
  left:   { style: 'thin', color: { rgb: '000000' } },
  right:  { style: 'thin', color: { rgb: '000000' } },
};
const thickBorder = {
  top:    { style: 'medium', color: { rgb: '000000' } },
  bottom: { style: 'medium', color: { rgb: '000000' } },
  left:   { style: 'medium', color: { rgb: '000000' } },
  right:  { style: 'medium', color: { rgb: '000000' } },
};

const S = {
  title:    { font: { bold: true, sz: 16, color: { rgb: '1F4E79' } }, alignment: { horizontal: 'left' } },
  subTitle: { font: { bold: true, sz: 12, color: { rgb: '2E75B6' } } },
  label:    { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: 'D6E4F0' } }, border, alignment: { horizontal: 'left' } },
  value:    { font: { sz: 10 }, border, alignment: { horizontal: 'left' } },
  hdrBlue:  { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F4E79' } }, border, alignment: { horizontal: 'center', wrapText: true } },
  hdrMTN:   { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'C55A11' } }, border, alignment: { horizontal: 'center', wrapText: true } },
  hdrEM:    { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '375623' } }, border, alignment: { horizontal: 'center', wrapText: true } },
  hdrNCD:   { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F4E79' } }, border: thickBorder, alignment: { horizontal: 'center' } },
  hdrImmun: { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '7030A0' } }, border: thickBorder, alignment: { horizontal: 'center' } },
  hdrSRH:   { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '833C00' } }, border: thickBorder, alignment: { horizontal: 'center' } },
  hdrCombined: { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '243F60' } }, border: thickBorder, alignment: { horizontal: 'center' } },
  rowMTN:   { font: { sz: 10 }, fill: { fgColor: { rgb: 'FCE4D6' } }, border, alignment: { horizontal: 'center' } },
  rowEM:    { font: { sz: 10 }, fill: { fgColor: { rgb: 'E2EFDA' } }, border, alignment: { horizontal: 'center' } },
  rowNCD:   { font: { sz: 10 }, fill: { fgColor: { rgb: 'DEEAF1' } }, border, alignment: { horizontal: 'center' } },
  rowImmun: { font: { sz: 10 }, fill: { fgColor: { rgb: 'EAD1DC' } }, border, alignment: { horizontal: 'center' } },
  rowLeft:  { font: { sz: 10 }, fill: { fgColor: { rgb: 'F2F2F2' } }, border, alignment: { horizontal: 'left' } },
  total:    { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F4E79' } }, border: thickBorder, alignment: { horizontal: 'center' } },
  totalMTN: { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'C55A11' } }, border: thickBorder, alignment: { horizontal: 'center' } },
  totalEM:  { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '375623' } }, border: thickBorder, alignment: { horizontal: 'center' } },
  ok:       { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '375623' } }, border, alignment: { horizontal: 'center' } },
  warn:     { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'FF0000' } }, border, alignment: { horizontal: 'center' } },
  caution:  { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'FF8C00' } }, border, alignment: { horizontal: 'center' } },
  region:   { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '2E75B6' } }, border, alignment: { horizontal: 'left' } },
  facility: { font: { sz: 10 }, fill: { fgColor: { rgb: 'DEEAF1' } }, border, alignment: { horizontal: 'left' } },
  facTotal: { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: 'BDD7EE' } }, border, alignment: { horizontal: 'left' } },
  grandTotal: { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '243F60' } }, border: thickBorder, alignment: { horizontal: 'center' } },
};

const COLS = ['A','B','C','D','E','F','G','H','I','J','K','L'];
const sc = (ws, addr, value, style) => {
  ws[addr] = { v: value ?? '', t: typeof value === 'number' ? 'n' : 's', s: style };
};

export default function ReportExport() {
  const [open,    setOpen]    = useState(false);
  const [tab,     setTab]     = useState(0);
  const [dateFrom,setFrom]    = useState('');
  const [dateTo,  setTo]      = useState('');
  const [loading, setLoading] = useState(false);

  // ── SMS Usage Report ─────────────────────────────────────────
  const downloadSMSUsage = async () => {
    const r = await getSMSReport(dateFrom, dateTo);
    const { summary, facilities } = r.data;
    const wb = XLSX.utils.book_new();
    const ws = {};
    ws['!merges'] = [];

    // Title
    sc(ws,'A1','SMS Usage by Category', S.title);
    ws['!merges'].push({s:{r:0,c:0},e:{r:0,c:6}});

    // Period
    sc(ws,'A3','Period From:', S.label); sc(ws,'B3', dateFrom, S.value);
    sc(ws,'A4','Period To:',   S.label); sc(ws,'B4', dateTo,   S.value);

    // Summary headers
    sc(ws,'A6','SMS Usage by Category', S.hdrBlue);
    ws['!merges'].push({s:{r:5,c:0},e:{r:5,c:6}});

    const sumHdrs = ['Uncompleted Enrolments','Completed Enrolments','Enrolment SMS',
      'Reminder Messages','Post Visit Followup','Missed Visit Followup','Health Messages'];
    sumHdrs.forEach((h,i) => sc(ws,`${COLS[i]}7`, h, S.hdrBlue));

    const sumVals = [
      parseInt(summary.uncompleted_enrolments||0), parseInt(summary.completed_enrolments||0),
      parseInt(summary.enrolment_sms||0),           parseInt(summary.reminders||0),
      parseInt(summary.post_visit_followup||0),      parseInt(summary.missed_visit_followup||0),
      parseInt(summary.health_messages||0),
    ];
    sumVals.forEach((v,i) => sc(ws,`${COLS[i]}8`, v, i===0 ? S.caution : S.total));

    // Facility section
    sc(ws,'A10','ENROLMENT BY FACILITY', S.hdrBlue);
    ws['!merges'].push({s:{r:9,c:0},e:{r:9,c:1}});
    sc(ws,'A11','Facility', S.hdrBlue);
    sc(ws,'B11','Enrolments', S.hdrBlue);

    const byRegion = {};
    facilities
      .filter(f => f.facility && f.facility.length > 2 && isNaN(f.facility))
      .forEach(f => {
        const reg = f.region || 'Unknown';
        if (!byRegion[reg]) byRegion[reg] = [];
        byRegion[reg].push(f);
      });

    let row = 12;
    Object.entries(byRegion).sort().forEach(([region, facs]) => {
      sc(ws,`A${row}`, region, S.region);
      sc(ws,`B${row}`, '', S.region);
      ws['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:1}});
      row++;
      facs.forEach(f => {
        sc(ws,`A${row}`, f.facility, S.facility);
        sc(ws,`B${row}`, parseInt(f.enrolments||0), {...S.facility, alignment:{horizontal:'center'}});
        row++;
      });
      const regTotal = facs.reduce((s,f) => s+parseInt(f.enrolments||0), 0);
      sc(ws,`A${row}`, `${region} Subtotal`, S.facTotal);
      sc(ws,`B${row}`, regTotal, {...S.facTotal, alignment:{horizontal:'center'}});
      row++;
    });

    const grand = facilities.reduce((s,f) => s+parseInt(f.enrolments||0), 0);
    sc(ws,`A${row}`, 'GRAND TOTAL', S.grandTotal);
    sc(ws,`B${row}`, grand, S.grandTotal);

    ws['!cols'] = [{wch:35},{wch:22},{wch:14},{wch:20},{wch:22},{wch:22},{wch:18}];
    ws['!rows'] = [{hpt:28},{},{hpt:18},{hpt:18},{},{hpt:20},{hpt:40},{hpt:24}];
    ws['!ref']  = `A1:G${row}`;
    XLSX.utils.book_append_sheet(wb, ws, 'SMS Usage');

    const buf = XLSX.write(wb, {bookType:'xlsx', type:'array'});
    saveAs(new Blob([buf],{type:'application/octet-stream'}),
      `SMS_usage__${dateFrom}__${dateTo}.xlsx`);
  };

  // ── Package Consumption Report ───────────────────────────────
  const downloadPackage = async () => {
    const r = await getPackageReport(dateFrom, dateTo);
    const { ncd, immunization, monthly } = r.data;
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Summary ──────────────────────────────────────
    const ws1 = {};
    ws1['!merges'] = [];

    sc(ws1,'A1','TextWell4Health SMS Package Consumption Report: By Use Case', S.title);
    ws1['!merges'].push({s:{r:0,c:0},e:{r:0,c:7}});
    sc(ws1,'A3','Period From:', S.label); sc(ws1,'B3', dateFrom, S.value);
    sc(ws1,'A4','Period To:',   S.label); sc(ws1,'B4', dateTo,   S.value);

    const colHdrs = ['Telecom','Msgs Out','Msgs In','Segments Out','Segments In',
      'Total Segments','Package / Cost Info','Status'];

    let row = 6;

    // ── NCD Section ──
    sc(ws1,`A${row}`,'NCD (Dokotela): doctor / dokotela keyword', S.hdrNCD);
    ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:7}});
    row++;
    colHdrs.forEach((h,i) => sc(ws1,`${COLS[i]}${row}`, h, S.hdrBlue));
    row++;

    ncd.forEach(n => {
      const segs = parseInt(n.total_segments||0);
      const rs = n.telecom==='MTN' ? S.rowMTN : S.rowEM;
      const rsL = {...rs, alignment:{horizontal:'left'}};
      sc(ws1,`A${row}`, n.telecom, rsL);
      sc(ws1,`B${row}`, parseInt(n.msgs_out||0), rs);
      sc(ws1,`C${row}`, parseInt(n.msgs_in||0),  rs);
      sc(ws1,`D${row}`, parseInt(n.segments_out||0), rs);
      sc(ws1,`E${row}`, parseInt(n.segments_in||0),  rs);
      sc(ws1,`F${row}`, segs, {...rs, font:{...rs.font, bold:true}});
      if (n.telecom==='MTN') {
        const pct   = parseFloat(n.package_used_pct||0);
        const limit = parseInt(n.monthly_limit_total||10000);
        const fee   = parseInt(n.total_budget||2000);
        sc(ws1,`G${row}`,
          `${segs} of ${limit.toLocaleString()} segments (${pct}% used: ${parseInt(n.remaining||0).toLocaleString()} remaining) | Budget: E${fee.toLocaleString()}`, rsL);
        sc(ws1,`H${row}`, pct > 90 ? 'CRITICAL' : pct > 70 ? 'WARNING' : 'OK',
          pct > 90 ? S.warn : pct > 70 ? S.caution : S.ok);
      } else if (n.telecom==='Eswatini Mobile') {
        sc(ws1,`G${row}`,
          `E${parseInt(n.fixed_cost||750).toLocaleString()} fixed + E${n.variable_cost||0} variable = E${n.total_cost||0} total`, rsL);
        sc(ws1,`H${row}`, 'ACTIVE', S.ok);
      } else {
        sc(ws1,`G${row}`, n.note||'Unclassified', rsL);
        sc(ws1,`H${row}`, 'OTHER', S.label);
      }
      row++;
    });

    const ncdMTNsegs = parseInt(ncd.find(n=>n.telecom==='MTN')?.total_segments||0);
    const ncdEMsegs  = parseInt(ncd.find(n=>n.telecom==='Eswatini Mobile')?.total_segments||0);
    sc(ws1,`A${row}`,'NCD TOTAL', S.total);
    sc(ws1,`B${row}`, parseInt(ncd.reduce((s,n)=>s+parseInt(n.msgs_out||0),0)), S.total);
    sc(ws1,`C${row}`, parseInt(ncd.reduce((s,n)=>s+parseInt(n.msgs_in||0),0)),  S.total);
    sc(ws1,`F${row}`, ncdMTNsegs+ncdEMsegs, S.total);
    ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:0}});
    row += 2;

    // ── Immunization Section ──
    sc(ws1,`A${row}`,'Immunization (Make): EPI / vaccination flows', S.hdrImmun);
    ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:7}});
    row++;
    colHdrs.forEach((h,i) => sc(ws1,`${COLS[i]}${row}`, h, S.hdrBlue));
    row++;

    if (immunization.length === 0) {
      sc(ws1,`A${row}`, 'No immunization messages in this period', S.rowLeft);
      ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:7}});
      row++;
    } else {
      immunization.forEach(n => {
        const segs = parseInt(n.total_segments||0);
        const rs = n.telecom==='MTN' ? S.rowMTN : S.rowEM;
        const rsL = {...rs, alignment:{horizontal:'left'}};
        sc(ws1,`A${row}`, n.telecom, rsL);
        sc(ws1,`B${row}`, parseInt(n.msgs_out||0), rs);
        sc(ws1,`C${row}`, parseInt(n.msgs_in||0),  rs);
        sc(ws1,`D${row}`, parseInt(n.segments_out||0), rs);
        sc(ws1,`E${row}`, parseInt(n.segments_in||0),  rs);
        sc(ws1,`F${row}`, segs, {...rs, font:{...rs.font, bold:true}});
        if (n.telecom==='MTN') {
          const pct   = parseFloat(n.package_used_pct||0);
          const limit = parseInt(n.monthly_limit_total||10000);
          const fee   = parseInt(n.total_budget||2000);
          sc(ws1,`G${row}`,
            `${segs} of ${limit.toLocaleString()} segments (${pct}% used: ${parseInt(n.remaining||0).toLocaleString()} remaining) | Budget: E${fee.toLocaleString()}`, rsL);
          sc(ws1,`H${row}`, pct>90?'CRITICAL':pct>70?'WARNING':'OK', pct>90?S.warn:pct>70?S.caution:S.ok);
        } else if (n.telecom==='Eswatini Mobile') {
          sc(ws1,`G${row}`,
            `E${parseInt(n.fixed_cost||750).toLocaleString()} fixed + E${n.variable_cost||0} variable = E${n.total_cost||0} total`, rsL);
          sc(ws1,`H${row}`, 'ACTIVE', S.ok);
        } else {
          sc(ws1,`G${row}`, n.note||'Unclassified', rsL);
          sc(ws1,`H${row}`, 'OTHER', S.label);
        }
        row++;
      });
    }

    const immunMTNsegs = parseInt(immunization.find(n=>n.telecom==='MTN')?.total_segments||0);
    const immunEMsegs  = parseInt(immunization.find(n=>n.telecom==='Eswatini Mobile')?.total_segments||0);
    sc(ws1,`A${row}`,'IMMUNIZATION TOTAL', {...S.total, fill:{fgColor:{rgb:'7030A0'}}});
    sc(ws1,`F${row}`, immunMTNsegs+immunEMsegs, {...S.total, fill:{fgColor:{rgb:'7030A0'}}});
    row += 2;

    // ── SRH Section ──
    sc(ws1,`A${row}`,'SRH (HIUD): coming soon', S.hdrSRH);
    ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:7}});
    row++;
    sc(ws1,`A${row}`,'Flows pending: will be activated when HIUD keyword is configured', S.rowLeft);
    ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:7}});
    row += 2;

    // ── Combined Summary ──
    const totalMTN = ncdMTNsegs + immunMTNsegs;
    const totalEM  = ncdEMsegs  + immunEMsegs;
    const emCost   = 750 + (totalEM * 0.10);

    sc(ws1,`A${row}`,'COMBINED PACKAGE SUMMARY: ALL USE CASES', S.hdrCombined);
    ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:7}});
    row++;

    ['Telecom','NCD Segments','Immunization Segments','SRH Segments','TOTAL Segments',
     'Package Limit','% of Package Used','Balance / Cost'].forEach((h,i) =>
      sc(ws1,`${COLS[i]}${row}`, h, S.hdrBlue));
    row++;

    const mtnPct = parseFloat(((totalMTN/10000)*100).toFixed(1));
    sc(ws1,`A${row}`,'MTN', {...S.rowMTN, alignment:{horizontal:'left'}});
    sc(ws1,`B${row}`, ncdMTNsegs, S.rowMTN);
    sc(ws1,`C${row}`, immunMTNsegs, S.rowMTN);
    sc(ws1,`D${row}`, 0, S.rowMTN);
    sc(ws1,`E${row}`, totalMTN, {...S.rowMTN, font:{bold:true,sz:11}});
    sc(ws1,`F${row}`, 10000, S.rowMTN);
    const mtnMonths = ncd[0]?.months || immunization[0]?.months || 1;
    const mtnTotalLimit  = 10000 * mtnMonths;
    const mtnTotalBudget = 2000  * mtnMonths;
    const emTotalFixed   = 750   * mtnMonths;
    sc(ws1,`G${row}`, `${mtnPct}% of ${mtnTotalLimit.toLocaleString()} limit | Budget: E${mtnTotalBudget.toLocaleString()}`,
      mtnPct>90?S.warn:mtnPct>70?S.caution:S.ok);
    sc(ws1,`H${row}`, `${(mtnTotalLimit-totalMTN).toLocaleString()} segments remaining`, S.rowMTN);
    row++;

    sc(ws1,`A${row}`,'Eswatini Mobile', {...S.rowEM, alignment:{horizontal:'left'}});
    sc(ws1,`B${row}`, ncdEMsegs, S.rowEM);
    sc(ws1,`C${row}`, immunEMsegs, S.rowEM);
    sc(ws1,`D${row}`, 0, S.rowEM);
    sc(ws1,`E${row}`, totalEM, {...S.rowEM, font:{bold:true,sz:11}});
    sc(ws1,`F${row}`, 'Pay As You Go', S.rowEM);
    const emFixedTotal = emTotalFixed||750;
    const emVarCost    = parseFloat((totalEM*0.10).toFixed(2));
    const emTotalCost  = parseFloat((emFixedTotal + emVarCost).toFixed(2));
    sc(ws1,`G${row}`, `E${emTotalCost.toFixed(2)} total`, S.rowEM);
    sc(ws1,`H${row}`, `E${emFixedTotal} fixed (E750 x ${mtnMonths} months) + E${emVarCost} variable`, S.rowEM);
    row++;

    // Grand total row
    sc(ws1,`A${row}`,'TOTAL ALL TELECOMS', S.grandTotal);
    sc(ws1,`E${row}`, totalMTN+totalEM, S.grandTotal);
    ws1['!merges'].push({s:{r:row-1,c:0},e:{r:row-1,c:3}},{s:{r:row-1,c:5},e:{r:row-1,c:7}});

    ws1['!cols'] = [{wch:22},{wch:14},{wch:14},{wch:16},{wch:16},{wch:18},{wch:28},{wch:30}];
    ws1['!ref']  = `A1:H${row}`;
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

    // ── Sheet 2: Monthly Breakdown ────────────────────────────
    const ws2 = {};
    ws2['!merges'] = [];
    sc(ws2,'A1','Monthly SMS Consumption: By Use Case & Telecom', S.title);
    ws2['!merges'].push({s:{r:0,c:0},e:{r:0,c:9}});

    const mHdrs = ['Month','Use Case','Telecom','Msgs Out','Msgs In',
      'Segments Out','Segments In','Total Segments','MTN % of Package','EM Monthly Cost (E)'];
    mHdrs.forEach((h,i) => sc(ws2,`${COLS[i]}3`, h, S.hdrBlue));

    let mrow = 4;
    monthly.forEach(m => {
      const segs = parseInt(m.total_segments||0);
      const isNCD   = m.use_case === 'NCD';
      const rs = isNCD
        ? (m.telecom==='MTN' ? S.rowNCD : {...S.rowNCD, fill:{fgColor:{rgb:'BDD7EE'}}})
        : (m.telecom==='MTN' ? S.rowImmun : {...S.rowImmun, fill:{fgColor:{rgb:'D9B3FF'}}});
      const rsL = {...rs, alignment:{horizontal:'left'}};

      sc(ws2,`A${mrow}`, m.month, rsL);
      sc(ws2,`B${mrow}`, m.use_case, rsL);
      sc(ws2,`C${mrow}`, m.telecom, rsL);
      sc(ws2,`D${mrow}`, parseInt(m.msgs_out||0), rs);
      sc(ws2,`E${mrow}`, parseInt(m.msgs_in||0),  rs);
      sc(ws2,`F${mrow}`, parseInt(m.segments_out||0), rs);
      sc(ws2,`G${mrow}`, parseInt(m.segments_in||0),  rs);
      sc(ws2,`H${mrow}`, segs, {...rs, font:{...rs.font, bold:true}});

      if (m.telecom==='MTN') {
        const pct = parseFloat(((segs/10000)*100).toFixed(1));
        sc(ws2,`I${mrow}`, `${pct}%`, pct>90?S.warn:pct>70?S.caution:S.ok);
        sc(ws2,`J${mrow}`, '', rs);
      } else {
        sc(ws2,`I${mrow}`, '', rs);
        sc(ws2,`J${mrow}`, `E${(750+(segs*0.10)).toFixed(2)}`, {...rs, font:{...rs.font, bold:true}});
      }
      mrow++;
    });

    ws2['!cols'] = [{wch:12},{wch:16},{wch:18},{wch:12},{wch:10},
      {wch:14},{wch:13},{wch:15},{wch:18},{wch:18}];
    ws2['!rows'] = [{hpt:28},{},{hpt:36}];
    ws2['!ref']  = `A1:J${mrow}`;
    XLSX.utils.book_append_sheet(wb, ws2, 'Monthly Breakdown');

    // ── Sheet 3: Cross-Use-Case Contacts ──────────────────
    const cross = r.data.crossContacts || [];
    if (cross.length > 0) {
      const ws3 = {};
      ws3['!merges'] = [];
      sc(ws3,'A1','Contacts Enrolled in Multiple Use Cases: NCD + Immunization', S.title);
      ws3['!merges'].push({s:{r:0,c:0},e:{r:0,c:8}});

      const cHdrs = ['Phone','Telecom','Region','Facility',
        'NCD Msgs','NCD Segments','Immun Msgs','Immun Segments','Total Segments'];
      cHdrs.forEach((h,i) => sc(ws3,`${COLS[i]}3`, h, S.hdrBlue));

      let crow = 4;
      cross.forEach(ct => {
        const ncdSegs   = parseInt(ct.ncd_segments||0);
        const immunSegs = parseInt(ct.immun_segments||0);
        const rs = ct.telecom==='MTN' ? S.rowMTN : S.rowEM;
        const rsL = {...rs, alignment:{horizontal:'left'}};
        sc(ws3,`A${crow}`, ct.phone, rsL);
        sc(ws3,`B${crow}`, ct.telecom, rsL);
        sc(ws3,`C${crow}`, ct.region, rsL);
        sc(ws3,`D${crow}`, ct.facility, rsL);
        sc(ws3,`E${crow}`, parseInt(ct.ncd_msgs||0), rs);
        sc(ws3,`F${crow}`, ncdSegs, rs);
        sc(ws3,`G${crow}`, parseInt(ct.immun_msgs||0), rs);
        sc(ws3,`H${crow}`, immunSegs, rs);
        sc(ws3,`I${crow}`, ncdSegs+immunSegs, {...rs, font:{...rs.font, bold:true}});
        crow++;
      });

      // Totals
      sc(ws3,`A${crow}`,'TOTAL', S.grandTotal);
      sc(ws3,`F${crow}`, cross.reduce((s,ct)=>s+parseInt(ct.ncd_segments||0),0), S.grandTotal);
      sc(ws3,`H${crow}`, cross.reduce((s,ct)=>s+parseInt(ct.immun_segments||0),0), S.grandTotal);
      sc(ws3,`I${crow}`, cross.reduce((s,ct)=>s+parseInt(ct.ncd_segments||0)+parseInt(ct.immun_segments||0),0), S.grandTotal);
      ws3['!merges'].push({s:{r:crow-1,c:0},e:{r:crow-1,c:4}});

      ws3['!cols'] = [{wch:16},{wch:18},{wch:14},{wch:28},{wch:12},{wch:14},{wch:12},{wch:16},{wch:15}];
      ws3['!ref']  = `A1:I${crow}`;
      XLSX.utils.book_append_sheet(wb, ws3, 'Cross-Use-Case Contacts');
    }

    const buf = XLSX.write(wb, {bookType:'xlsx', type:'array'});
    saveAs(new Blob([buf],{type:'application/octet-stream'}),
      `Package_consumption__${dateFrom}__${dateTo}.xlsx`);
  };

  const handleDownload = async () => {
    setLoading(true);
    try { tab===0 ? await downloadSMSUsage() : await downloadPackage(); }
    catch(e) { console.error('Report error:', e); alert('Error: '+e.message); }
    setLoading(false);
    setOpen(false);
  };

  return (
    <>
      <Button variant="outlined" startIcon={<AssessmentOutlined/>} onClick={()=>setOpen(true)}
        sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.75rem',
          borderColor:'rgba(0,191,165,0.4)', color:'primary.main',
          '&:hover':{borderColor:'primary.main',bgcolor:'rgba(0,191,165,0.08)'} }}>
        Export Report
      </Button>

      <Dialog open={open} onClose={()=>setOpen(false)}
        PaperProps={{sx:{bgcolor:'#0d1117',border:'1px solid rgba(0,191,165,0.2)',borderRadius:2,minWidth:440}}}>
        <DialogTitle sx={{color:'primary.main',fontFamily:'"IBM Plex Mono",monospace'}}>
          <AssessmentOutlined sx={{mr:1,verticalAlign:'middle'}}/>
          Download Report
        </DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{mb:2,
            borderBottom:'1px solid rgba(255,255,255,0.1)',
            '& .MuiTab-root':{fontFamily:'"IBM Plex Mono",monospace',fontSize:'0.72rem',minHeight:40},
            '& .Mui-selected':{color:'primary.main !important'},
            '& .MuiTabs-indicator':{bgcolor:'primary.main'}}}>
            <Tab label="📋 SMS Usage" value={0}/>
            <Tab label="📦 Package Consumption" value={1}/>
          </Tabs>

          {tab===0 && <Typography variant="body2" color="text.secondary" mb={2}>
            Downloads SMS Usage Distribution Report: enrolment counts, message types and facility breakdown.
          </Typography>}

          {tab===1 && <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Package consumption separated by use case:
            </Typography>
            <Box sx={{pl:1, borderLeft:'2px solid rgba(0,191,165,0.3)', mb:1}}>
              <Typography variant="body2" color="text.secondary">
                🏥 <strong style={{color:'#4FC3F7'}}>NCD (Dokotela)</strong><br/>
                💉 <strong style={{color:'#CE93D8'}}>Immunization (Make)</strong><br/>
                🔬 <strong style={{color:'#EF9A9A'}}>SRH (HIUD)</strong>: coming soon
              </Typography>
            </Box>
            <Divider sx={{my:1,borderColor:'rgba(255,255,255,0.1)'}}/>
            <Typography variant="body2" color="text.secondary">
              📦 <strong style={{color:'#FFB300'}}>MTN</strong>: 10,000 SMS/month · resets monthly<br/>
              💳 <strong style={{color:'#00BFA5'}}>Eswatini Mobile</strong>: E750/month + E0.10/segment
            </Typography>
          </Box>}

          <Box display="flex" gap={2} flexDirection="column">
            <TextField label="From" type="date" value={dateFrom} onChange={e=>setFrom(e.target.value)}
              InputLabelProps={{shrink:true}} fullWidth size="small"/>
            <TextField label="To" type="date" value={dateTo} onChange={e=>setTo(e.target.value)}
              InputLabelProps={{shrink:true}} fullWidth size="small"/>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2,gap:1}}>
          <Button onClick={()=>setOpen(false)} color="inherit" size="small">Cancel</Button>
          <Button variant="contained" onClick={handleDownload}
            disabled={loading||!dateFrom||!dateTo}
            startIcon={loading?<CircularProgress size={16}/>:<DownloadOutlined/>}
            sx={{fontFamily:'"IBM Plex Mono",monospace'}}>
            {loading?'Generating...':'Download Excel'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
